/**
 * Add to Calendar
 * Any .js-add-to-calendar link generates and downloads a standard .ics file
 * (compatible with Apple Calendar, Outlook, and Google Calendar import).
 *
 * Event details are read from data attributes on the link:
 *   data-event-title        (required)
 *   data-event-start        (required) local datetime, e.g. "2026-12-01T08:00:00"
 *   data-event-end          (optional) defaults to 1 hour after start
 *   data-event-location     (optional)
 *   data-event-description  (optional)
 *   data-event-allday       (optional) "true" for an all-day event (date only)
 */
export function addToCalendar() {

    //Format a Date as an iCalendar local datetime: YYYYMMDDTHHMMSS
    function formatDateTime(date) {
        const pad = (n) => String(n).padStart(2, '0');
        return date.getFullYear() +
            pad(date.getMonth() + 1) +
            pad(date.getDate()) + 'T' +
            pad(date.getHours()) +
            pad(date.getMinutes()) +
            pad(date.getSeconds());
    }

    //Format a Date as an iCalendar date value: YYYYMMDD (all-day events)
    function formatDate(date) {
        const pad = (n) => String(n).padStart(2, '0');
        return date.getFullYear() + pad(date.getMonth() + 1) + pad(date.getDate());
    }

    //Escape reserved characters per RFC 5545.
    function escapeText(str) {
        return String(str)
            .replace(/\\/g, '\\\\')
            .replace(/;/g, '\\;')
            .replace(/,/g, '\\,')
            .replace(/\r?\n/g, '\\n');
    }

    function buildIcs(data) {
        const start = new Date(data.start);
        let end = data.end ? new Date(data.end) : null;
        if (!end || isNaN(end)) {
            end = new Date(start.getTime() + 60 * 60 * 1000); //+1 hour
        }

        const lines = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//LMCU//Add to Calendar//EN',
            'CALSCALE:GREGORIAN',
            'BEGIN:VEVENT',
            'UID:' + formatDateTime(new Date()) + '-' + Math.round(start.getTime()) + '@lmcu',
            'DTSTAMP:' + formatDateTime(new Date())
        ];

        if (data.allday) {
            //All-day: DTEND is exclusive, so add one day.
            const endExclusive = new Date(end.getTime() + 24 * 60 * 60 * 1000);
            lines.push('DTSTART;VALUE=DATE:' + formatDate(start));
            lines.push('DTEND;VALUE=DATE:' + formatDate(endExclusive));
        } else {
            lines.push('DTSTART:' + formatDateTime(start));
            lines.push('DTEND:' + formatDateTime(end));
        }

        lines.push('SUMMARY:' + escapeText(data.title));
        if (data.location) {
            lines.push('LOCATION:' + escapeText(data.location));
        }
        if (data.description) {
            lines.push('DESCRIPTION:' + escapeText(data.description));
        }
        lines.push('END:VEVENT', 'END:VCALENDAR');

        return lines.join('\r\n');
    }

    function download(filename, ics) {
        const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const $link = $('<a>')
            .attr({ href: url, download: filename })
            .css('display', 'none')
            .appendTo('body');
        $link[0].click();
        $link.remove();
        //Release the object URL after the click has been handled.
        setTimeout(function() { URL.revokeObjectURL(url); }, 1000);
    }

    $(document).on('click', '.js-add-to-calendar', function(e) {
        e.preventDefault();

        const $link = $(this);
        const data = {
            title: $link.attr('data-event-title') || 'Event',
            start: $link.attr('data-event-start'),
            end: $link.attr('data-event-end'),
            location: $link.attr('data-event-location'),
            description: $link.attr('data-event-description'),
            allday: $link.attr('data-event-allday') === 'true'
        };

        if (!data.start || isNaN(new Date(data.start))) {
            return; //No valid start date — nothing to add.
        }

        const filename = data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'event';
        download(filename + '.ics', buildIcs(data));
    });
}
